import {
  ProgramScheduleDTO,
  SubscriptionDTO,
  SwapType,
  UpgradeType,
} from "@campgladiator/cg-common.types.types";
import { ButtonProps } from "@campgladiator/cgui-core.atoms.button";
import { Loader } from "@campgladiator/cgui-core.atoms.loader";
import React, { useCallback } from "react";
import { Step } from "./useWithUpgradeMembershipModal";
import styles from "./UpgradeMembershipModal.module.scss";

export type UpgradeMembershipModalButtonsProps = {
  areAnyLoading: boolean;
  areEligibleProductsLoading: boolean;
  currentSubscription: SubscriptionDTO;
  handleOnSubmitUpgrade: () => Promise<boolean>;
  hasTrainerGrants: boolean;
  isPriceLoading: boolean;
  isPrimaryButtonDisabled: boolean;
  isTrainerAndScheduleStepsVisible: boolean;
  onClose: (success?: boolean) => void;
  productName: string;
  productPrice: string;
  selectScheduleOptions?: ProgramScheduleDTO[];
  setStep: (step: Step) => void;
  setSwapType: (swapType: SwapType) => void;
  setUpgradeType: (upgradeType: UpgradeType) => void;
  step: Step;
  swapType?: SwapType;
  theme: ButtonProps["theme"];
  upgradeType: UpgradeType;
};

const useUpgradeMembershipModalButtons = ({
  areEligibleProductsLoading,
  upgradeType,
  step,
  setStep,
  setSwapType,
  isTrainerAndScheduleStepsVisible,
  selectScheduleOptions,
  hasTrainerGrants,
  onClose,
  setUpgradeType,
  swapType,
  currentSubscription,
  areAnyLoading,
  isPrimaryButtonDisabled,
  isPriceLoading,
  productName,
  productPrice,
  handleOnSubmitUpgrade,
  theme,
}: UpgradeMembershipModalButtonsProps) => {
  const confirmUpgrade = useCallback(async () => {
    await handleOnSubmitUpgrade()
      .then((status) => (status ? setStep("upgradeSuccess") : onClose(false)))
      .catch((err) => {
        onClose(false);
      });
  }, [handleOnSubmitUpgrade, onClose]);

  const handleOnSelectUpgradeType = (type: UpgradeType) => {
    setUpgradeType(type);
    if (type === "SWAP") goToSelectSwapType();
    else setStep("selectProduct");
  };
  const isSelectOrSwapTypeStep = ["selectType", "selectSwapType"].includes(
    step
  );

  const getPrimaryButtonAction = () => {
    switch (step) {
      case "selectType":
        return upgradeType === "SWAP"
          ? goToSelectSwapType()
          : setStep("selectProduct");
      case "selectProduct":
        return isTrainerAndScheduleStepsVisible
          ? setStep("selectTrainer")
          : setStep("confirmUpgrade");
      case "selectTrainer":
        return selectScheduleOptions?.length > 0
          ? setStep("selectSchedule")
          : setStep("confirmUpgrade");
      case "selectSchedule":
        return setStep("confirmUpgrade");
      case "confirmUpgrade":
        return hasTrainerGrants
          ? setStep("termsAndConditions")
          : confirmUpgrade();
      case "termsAndConditions":
        return confirmUpgrade();
      case "upgradeSuccess":
        return onClose(true);
      default:
        return () => {};
    }
  };

  const goToSelectType = () => {
    setUpgradeType(null);
    setSwapType(null);
    setStep("selectType");
  };

  const goToSelectSwapType = () => {
    setStep("selectSwapType");
    setSwapType(null);
  };

  const onCloseModal = (success?: boolean) => {
    setUpgradeType(null);
    setSwapType(null);
    onClose(success);
  };

  const getSecondaryButtonAction = () => {
    switch (step) {
      case "selectType":
        return onClose(undefined);
      case "selectSwapType":
        return goToSelectType();
      case "selectProduct":
        return upgradeType === "SWAP" ? goToSelectSwapType() : goToSelectType();
      case "selectTrainer":
        return upgradeType === "SWAP" && swapType === "ROLLOVER"
          ? goToSelectSwapType()
          : setStep("selectProduct");
      case "selectSchedule":
        return setStep("selectTrainer");
      case "confirmUpgrade":
        return upgradeType === "SWAP" &&
          swapType === "ROLLOVER" &&
          currentSubscription.productSubType === "IN_PERSON"
          ? goToSelectSwapType()
          : isTrainerAndScheduleStepsVisible
          ? selectScheduleOptions?.length
            ? setStep("selectSchedule")
            : setStep("selectTrainer")
          : setStep("selectProduct");
      case "termsAndConditions":
        return setStep("confirmUpgrade");
      default:
        return () => {};
    }
  };

  const getPrimaryButtonLabel = (step: Step, areAnyLoading: boolean) => {
    if (areAnyLoading) {
      return <Loader />;
    }
    switch (step) {
      case "confirmUpgrade":
        return hasTrainerGrants ? "Next" : "Confirm";
      case "termsAndConditions":
        return "Confirm";
      case "upgradeSuccess":
        return "Got It";
      default:
        return "Next";
    }
  };

  const getSecondaryButtonLabel = (step: Step) => {
    switch (step) {
      case "selectType":
        return "Cancel";
      case "selectSwapType":
        return "Back";
      default:
        return "Back";
    }
  };

  const getSecondaryButtonVariation = (step: Step) => {
    switch (step) {
      default:
        return "text" as ButtonProps["variation"];
    }
  };

  const getPrimaryButtonVariation = (step: Step) => {
    switch (step) {
      case "selectSwapType":
        return "text" as ButtonProps["variation"];
      default:
        return undefined;
    }
  };

  const primaryButtonStepOptions: ButtonProps = {
    emphasis: "primary",
    variation: getPrimaryButtonVariation(step),
    children: getPrimaryButtonLabel(step, areAnyLoading),
    disabled: isPrimaryButtonDisabled || isPriceLoading,
    onClick: getPrimaryButtonAction,
    theme,
  };

  const primaryButtonOptions: ButtonProps = isSelectOrSwapTypeStep
    ? undefined
    : primaryButtonStepOptions;

  const secondaryButtonStepOptions: ButtonProps = {
    emphasis: "primary",
    disabled: isPriceLoading,
    variation: getSecondaryButtonVariation(step),
    children: getSecondaryButtonLabel(step),
    onClick: getSecondaryButtonAction,
    theme,
    className: isSelectOrSwapTypeStep ? styles.wrapLine : undefined,
  };
  const secondaryButtonOptions: ButtonProps | undefined =
    step === "upgradeSuccess" ? undefined : secondaryButtonStepOptions;

  const isSelectStepButtonDisabled =
    !productName ||
    !productPrice ||
    isPriceLoading ||
    areEligibleProductsLoading;

  const selectTypePrimaryButtonStepOptions: ButtonProps = {
    variation: "solid",
    emphasis: "primary",
    theme,
    children: step === "selectType" ? "Change Membership Term" : "Restart",
    disabled: isSelectStepButtonDisabled,
    onClick: () =>
      step == "selectType"
        ? handleOnSelectUpgradeType("UPGRADE")
        : setSwapType("RESTART"),
  };
  const selectTypePrimaryButtonOptions: ButtonProps | undefined =
    isSelectOrSwapTypeStep ? selectTypePrimaryButtonStepOptions : undefined;

  const selectTypeSecondaryButtonStepOptions: ButtonProps = {
    variation: "outline",
    emphasis: "primary",
    theme,
    children: step === "selectType" ? "swap membership type" : "Rollover",
    disabled: isSelectStepButtonDisabled,
    onClick: () =>
      step == "selectType"
        ? handleOnSelectUpgradeType("SWAP")
        : setSwapType("ROLLOVER"),
  };
  const selectTypeSecondaryButtonOptions: ButtonProps | undefined =
    isSelectOrSwapTypeStep ? selectTypeSecondaryButtonStepOptions : undefined;

  const modalButtons: ButtonProps[] = [
    selectTypePrimaryButtonOptions,
    selectTypeSecondaryButtonOptions,
    secondaryButtonOptions,
    primaryButtonOptions,
  ];

  return { onCloseModal, modalButtons };
};

export default useUpgradeMembershipModalButtons;
